import type {
  ContactAddress,
  ContactBirthday,
  ContactEmail,
  ContactNames,
  ContactOrganization,
  ContactPhone,
  ContactRelation,
  UnifiedContact,
} from "@/src/features/integrations/vendors/unified/contactTypes";
import { isAutomatedProviderContact } from "@/src/features/contacts/providerContactHygiene";
import { buildProviderAccountScopedRecordId } from "@/src/features/integrations/providerAccounts";

export {
  CrmProviderHttpError,
  fetchCrmJson,
} from "@/src/features/integrations/vendors/crm/transport";
export type {
  CrmFetchOptions,
} from "@/src/features/integrations/vendors/crm/transport";

export type CrmContactProvider = "hubspot" | "salesforce";

export type CrmContactAssociation = {
  providerObjectType: "company" | "account";
  providerObjectId: string;
  name?: string;
  relationship?: string;
};

export type CrmContactRouteKeys = {
  emails: string[];
  phones: string[];
};

export type UnifiedCrmContact = Omit<UnifiedContact, "vendor"> & {
  vendor: CrmContactProvider;
  providerContactId: string;
  providerAccountKey: string;
  providerAccountKeys: string[];
  sourceIdentity: string;
  sourceRevision: string;
  sourceRevisionAt: number;
  routeKeys: CrmContactRouteKeys;
  providerAssociations: CrmContactAssociation[];
};

export type CrmContactTenant = {
  userId: string;
  companyId: string;
  providerAccountKey: string;
};

export type BuildCrmContactInput = {
  provider: CrmContactProvider;
  providerContactId: string;
  tenant: CrmContactTenant;
  names: ContactNames;
  emailAddresses?: ContactEmail[];
  phoneNumbers?: ContactPhone[];
  organizations?: ContactOrganization[];
  addresses?: ContactAddress[];
  birthday?: ContactBirthday;
  photos?: string[];
  relations?: ContactRelation[];
  urls?: string[];
  notes?: string;
  isDeleted: boolean;
  createdDate?: number;
  updatedDate?: number;
  sourceRevision: string;
  sourceRevisionAt: number;
  providerAssociations?: CrmContactAssociation[];
};

function requiredIdentity(value: unknown, label: string): string {
  const normalized = String(value ?? "").trim();
  if (!normalized) throw new Error(`${label} is required for CRM contact sync`);
  return normalized;
}

function identitySegment(value: string): string {
  // Provider object IDs are opaque. Salesforce 15-character IDs in particular
  // are case-sensitive, so canonical identity must never fold their casing.
  return encodeURIComponent(value.trim());
}

export function normalizeContactEmail(value: unknown): string {
  const normalized = String(value ?? "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized) ? normalized : "";
}

export function normalizeContactPhone(value: unknown): string {
  const raw = String(value ?? "").trim();
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 7) return "";
  return `${raw.startsWith("+") ? "+" : ""}${digits}`;
}

export function buildCrmContactRouteKeys(args: {
  emailAddresses?: ContactEmail[];
  phoneNumbers?: ContactPhone[];
}): CrmContactRouteKeys {
  const emails = Array.from(new Set(
    (args.emailAddresses ?? [])
      .map((email) => normalizeContactEmail(email.value))
      .filter(Boolean),
  )).sort();
  const phones = Array.from(new Set(
    (args.phoneNumbers ?? [])
      .map((phone) => normalizeContactPhone(phone.value))
      .filter(Boolean),
  )).sort();
  return {
    emails: emails.map((email) => `email:${email}`),
    phones: phones.map((phone) => `phone:${phone}`),
  };
}

export function buildCrmContactSourceIdentity(args: {
  provider: CrmContactProvider;
  tenant: CrmContactTenant;
  providerContactId: string;
}): string {
  const userId = requiredIdentity(args.tenant.userId, "CRM contact userId");
  const companyId = requiredIdentity(args.tenant.companyId, "CRM contact companyId");
  const providerAccountKey = requiredIdentity(
    args.tenant.providerAccountKey,
    "CRM provider account key",
  );
  const providerContactId = requiredIdentity(
    args.providerContactId,
    "CRM provider contact ID",
  );
  const baseProvider = providerAccountKey.split(":")[0]?.toLowerCase();
  if (baseProvider !== args.provider) {
    throw new Error("CRM provider account key does not match the provider");
  }
  return [
    "contact",
    identitySegment(companyId),
    identitySegment(userId),
    identitySegment(providerAccountKey),
    identitySegment(providerContactId),
  ].join(":");
}

/**
 * Finish a provider payload as one account-scoped unified contact. Automated
 * email-only identities are rejected here before the canonical upsert path.
 */
export function buildUnifiedCrmContact(
  input: BuildCrmContactInput,
): UnifiedCrmContact | null {
  const providerContactId = requiredIdentity(
    input.providerContactId,
    "CRM provider contact ID",
  );
  const providerAccountKey = requiredIdentity(
    input.tenant.providerAccountKey,
    "CRM provider account key",
  );
  const userId = requiredIdentity(input.tenant.userId, "CRM contact userId");
  const companyId = requiredIdentity(input.tenant.companyId, "CRM contact companyId");
  const emailAddresses = (input.emailAddresses ?? [])
    .map((email) => ({ ...email, value: normalizeContactEmail(email.value) }))
    .filter((email) => Boolean(email.value));
  const phoneNumbers = (input.phoneNumbers ?? [])
    .map((phone) => ({ ...phone, value: String(phone.value ?? "").trim() }))
    .filter((phone) => Boolean(normalizeContactPhone(phone.value)));
  const sourceRevisionAt = Number.isFinite(input.sourceRevisionAt)
    ? Math.max(0, input.sourceRevisionAt)
    : 0;

  const contact: UnifiedCrmContact = {
    vendorId: buildProviderAccountScopedRecordId(providerAccountKey, providerContactId),
    providerContactId,
    providerAccountKey,
    providerAccountKeys: [providerAccountKey],
    names: input.names,
    emailAddresses,
    phoneNumbers,
    organizations: input.organizations ?? [],
    addresses: input.addresses ?? [],
    birthday: input.birthday,
    photos: input.photos ?? [],
    relations: input.relations ?? [],
    urls: input.urls ?? [],
    interests: [],
    locations: [],
    occupations: [],
    skills: [],
    notes: input.notes,
    isDeleted: input.isDeleted,
    companyId,
    userId,
    syncType: "contact",
    vendor: input.provider,
    lastSynced: Date.now(),
    createdDate: input.createdDate,
    updatedDate: input.updatedDate,
    sourceIdentity: buildCrmContactSourceIdentity({
      provider: input.provider,
      tenant: input.tenant,
      providerContactId,
    }),
    sourceRevision: requiredIdentity(input.sourceRevision, "CRM source revision"),
    sourceRevisionAt,
    routeKeys: buildCrmContactRouteKeys({ emailAddresses, phoneNumbers }),
    providerAssociations: input.providerAssociations ?? [],
  };

  if (!contact.isDeleted && isAutomatedProviderContact(contact)) return null;
  return contact;
}

function compareContactRevision(
  candidate: UnifiedCrmContact,
  existing: UnifiedCrmContact,
): number {
  if (candidate.sourceRevisionAt !== existing.sourceRevisionAt) {
    return candidate.sourceRevisionAt - existing.sourceRevisionAt;
  }
  if (candidate.isDeleted !== existing.isDeleted) {
    return candidate.isDeleted ? 1 : -1;
  }
  return candidate.sourceRevision.localeCompare(existing.sourceRevision);
}

/** Page overlap and webhook replay collapse to the newest source revision. */
export function dedupeCrmContacts(
  contacts: UnifiedCrmContact[],
): UnifiedCrmContact[] {
  const byIdentity = new Map<string, UnifiedCrmContact>();
  for (const contact of contacts) {
    const existing = byIdentity.get(contact.sourceIdentity);
    if (!existing || compareContactRevision(contact, existing) >= 0) {
      byIdentity.set(contact.sourceIdentity, contact);
    }
  }
  return Array.from(byIdentity.values());
}
